import { useEffect, useState } from 'react'
import { Button } from '@/components/ui/button'
import {
  DataTableAdvanced,
  type DataTableColumn,
  type DataTableFilter,
} from '@/components/ui/data-table-advanced'
import { Badge } from '@/components/ui/badge'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { ConfirmDialog } from '@/components/ui/confirm-dialog'
import {
  billingService,
  formatAmount,
  providerLabel,
  type BillingInvoice,
} from '@/services/billing'
import { format, isSameMonth } from 'date-fns'
import { toast } from 'sonner'
import { MoreHorizontal, ExternalLink, Undo2, Receipt, Calendar } from 'lucide-react'

const statusVariant = (
  status: BillingInvoice['status']
): 'default' | 'secondary' | 'destructive' | 'outline' => {
  switch (status) {
    case 'paid':
      return 'default'
    case 'failed':
      return 'destructive'
    case 'open':
      return 'secondary'
    default:
      return 'outline'
  }
}

export const BillingInvoices: React.FC = () => {
  const [invoices, setInvoices] = useState<BillingInvoice[]>([])
  const [loading, setLoading] = useState(true)
  const [refundTarget, setRefundTarget] = useState<BillingInvoice | null>(null)

  const fetchInvoices = async () => {
    try {
      setLoading(true)
      setInvoices(await billingService.listInvoices())
    } catch (err) {
      console.error('Failed to fetch invoices:', err)
      toast.error('Failed to load invoices')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchInvoices()
  }, [])

  const now = new Date()
  const paidThisMonthCents = invoices
    .filter(
      (i) => i.status === 'paid' && i.paid_at && isSameMonth(new Date(i.paid_at), now)
    )
    .reduce((sum, i) => sum + i.amount_cents, 0)
  const failed = invoices.filter((i) => i.status === 'failed').length
  const refunded = invoices.filter((i) => i.status === 'refunded').length

  const columns: DataTableColumn<BillingInvoice>[] = [
    {
      accessorKey: 'tenant_name',
      header: 'Tenant',
      cell: ({ row }) => (
        <div className="font-medium">{row.original.tenant_name || row.original.tenant_id}</div>
      ),
    },
    {
      accessorKey: 'amount_cents',
      header: 'Amount',
      cell: ({ row }) => formatAmount(row.original.amount_cents, row.original.currency),
    },
    {
      accessorKey: 'status',
      header: 'Status',
      cell: ({ row }) => (
        <Badge variant={statusVariant(row.original.status)}>{row.original.status}</Badge>
      ),
      filterFn: 'select',
    },
    {
      accessorKey: 'provider',
      header: 'Processor',
      cell: ({ row }) => (
        <Badge variant="outline">{providerLabel(row.original.provider)}</Badge>
      ),
      filterFn: 'select',
    },
    {
      accessorKey: 'issued_at',
      header: 'Issued',
      cell: ({ row }) =>
        row.original.issued_at ? (
          <div className="flex items-center gap-1 text-sm text-muted-foreground">
            <Calendar className="h-3 w-3" />
            {format(new Date(row.original.issued_at), 'MMM d, yyyy')}
          </div>
        ) : (
          <span className="text-sm text-muted-foreground">—</span>
        ),
    },
    {
      accessorKey: 'paid_at',
      header: 'Paid',
      cell: ({ row }) =>
        row.original.paid_at ? (
          <span className="text-sm text-muted-foreground">
            {format(new Date(row.original.paid_at), 'MMM d, yyyy')}
          </span>
        ) : (
          <span className="text-sm text-muted-foreground">—</span>
        ),
    },
  ]

  const filters: DataTableFilter[] = [
    {
      column: 'status',
      type: 'select',
      label: 'Status',
      options: [
        { label: 'Paid', value: 'paid' },
        { label: 'Open', value: 'open' },
        { label: 'Failed', value: 'failed' },
        { label: 'Refunded', value: 'refunded' },
      ],
    },
    {
      column: 'provider',
      type: 'select',
      label: 'Processor',
      options: [
        { label: 'Stripe', value: 'stripe' },
        { label: 'NMI', value: 'nmi' },
      ],
    },
  ]

  const handleConfirmRefund = async () => {
    if (!refundTarget) return
    const invoice = refundTarget
    setRefundTarget(null)
    try {
      await billingService.refundInvoice(invoice.id)
      toast.success(
        `Refunded ${formatAmount(invoice.amount_cents, invoice.currency)} to ${invoice.tenant_name || 'tenant'}`
      )
      fetchInvoices()
    } catch (err: unknown) {
      console.error('Failed to refund invoice:', err)
      const detail = (err as { response?: { data?: { detail?: string } } })?.response
        ?.data?.detail
      toast.error(detail || 'Failed to refund invoice')
    }
  }

  const renderActions = (invoice: BillingInvoice) => (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" className="h-8 w-8 p-0">
          <span className="sr-only">Open menu</span>
          <MoreHorizontal className="h-4 w-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuLabel>Actions</DropdownMenuLabel>
        {invoice.hosted_invoice_url && (
          <DropdownMenuItem
            onClick={() => window.open(invoice.hosted_invoice_url, '_blank')}
          >
            <ExternalLink className="mr-2 h-4 w-4" />
            Open hosted invoice
          </DropdownMenuItem>
        )}
        {invoice.status === 'paid' && (
          <DropdownMenuItem
            className="text-destructive"
            onClick={() => setRefundTarget(invoice)}
          >
            <Undo2 className="mr-2 h-4 w-4" />
            Refund
          </DropdownMenuItem>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  )

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-3xl font-bold text-foreground">Invoices</h1>
          <p className="text-muted-foreground mt-1">
            Payments across all processors — NMI invoices are synthesized from
            transaction records
          </p>
        </div>
      </div>

      <div className="grid gap-4 md:grid-cols-3 mb-6">
        <div className="rounded-lg border bg-card p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-muted-foreground">Paid This Month</p>
              <p className="text-2xl font-bold">{formatAmount(paidThisMonthCents)}</p>
            </div>
            <Receipt className="h-8 w-8 text-muted-foreground opacity-50" />
          </div>
        </div>
        <div className="rounded-lg border bg-card p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-muted-foreground">Failed</p>
              <p className="text-2xl font-bold">{failed}</p>
            </div>
            <Badge variant="destructive" className="h-6">Failed</Badge>
          </div>
        </div>
        <div className="rounded-lg border bg-card p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-muted-foreground">Refunded</p>
              <p className="text-2xl font-bold">{refunded}</p>
            </div>
            <Badge variant="outline" className="h-6">Refunded</Badge>
          </div>
        </div>
      </div>

      <DataTableAdvanced
        columns={columns}
        data={invoices}
        loading={loading}
        searchable={true}
        searchPlaceholder="Search invoices..."
        searchKeys={['tenant_name']}
        filters={filters}
        showColumnVisibility={true}
        showPagination={true}
        pageSize={10}
        pageSizeOptions={[10, 20, 30, 50]}
        actions={renderActions}
        emptyMessage="No invoices yet."
      />

      <ConfirmDialog
        open={refundTarget !== null}
        onOpenChange={(open) => {
          if (!open) setRefundTarget(null)
        }}
        title="Refund invoice?"
        description={`This refunds ${
          refundTarget ? formatAmount(refundTarget.amount_cents, refundTarget.currency) : ''
        } to ${refundTarget?.tenant_name || 'the tenant'} via ${
          refundTarget ? providerLabel(refundTarget.provider) : ''
        }. This cannot be undone.`}
        confirmLabel="Refund"
        destructive
        onConfirm={handleConfirmRefund}
      />
    </div>
  )
}
